import { deleteCookie, readCookie, writeCookie } from '../Utility/Cookie'

// Matomo runs cookieless on a legitimate-interest basis, so there is no consent to withdraw —
// what a visitor gets instead is this objection. The cookie stores nothing but the objection
// itself, which is why it needs no consent of its own (§ 25 Abs. 2 TDDDG).
const OPT_OUT_COOKIE = 'vhw_tracking_optout'
const OPT_OUT_MAX_AGE = 60 * 60 * 24 * 365

// matomo.js invokes this hook once it has loaded, whoever injected it — with a Tag Manager setup
// that is the container, not us. Chained rather than assigned, so a hook the container registered
// itself survives.
const onMatomoReady = (callback: () => void): void => {
  if (window.Matomo !== undefined) {
    callback()

    return
  }

  const previous = window.matomoAsyncInit

  window.matomoAsyncInit = (): void => {
    previous?.()
    callback()
  }
}

class TrackingOptOutService {
  public isOptedOut(): boolean {
    return readCookie(OPT_OUT_COOKIE) === '1'
  }

  public optIn(): void {
    deleteCookie(OPT_OUT_COOKIE)
    this.applyToTrackers(false)
  }

  public optOut(): void {
    writeCookie(OPT_OUT_COOKIE, '1', OPT_OUT_MAX_AGE)
    this.applyToTrackers(true)
  }

  /**
   * Gating our own data layer is not enough: the container's page view tag fires on a trigger,
   * without any `_mtm` push to intercept. Only Matomo's own opt-out stops that, and it persists
   * it in a cookie matomo.js reads at tracker init — so from the page view after the objection
   * on, nothing is sent at all. Re-asserted on every page view because Matomo's cookie can be
   * cleared independently of ours.
   */
  public syncTrackers(): void {
    if (this.isOptedOut()) {
      this.applyToTrackers(true)
    }
  }

  // Never via `window._paq`: with a Tag-Manager-loaded Matomo those calls run against an
  // unconfigured tracker instance — https://github.com/matomo-org/tag-manager/issues/300
  private applyToTrackers(optedOut: boolean): void {
    onMatomoReady((): void => {
      for (const tracker of window.Matomo?.getAsyncTrackers() ?? []) {
        if (optedOut) {
          tracker.optUserOut()
        } else {
          tracker.forgetUserOptOut()
        }
      }
    })
  }
}

export default new TrackingOptOutService()
